import React, { ReactNode, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
  selectTwilioUserEntities,
  TwilioMessage,
  MessageType,
  MessageDirection,
  VideoCallMessageType,
} from 'models/twilio';
import moment from 'moment';
import isNil from 'lodash/isNil';
import useCurrentAttendee from './useCurrentAttendee';
import useFirebaseChatConfig from './useFirebaseChatConfig';
import DefaultBubbleWrapper from '../components/messages/DefaultBubbleWrapper';
import ContactCard from '../components/messages/ContactCard';
import VideoCallMessage from '../components/messages/VideoCallMessage';
import SystemMessage from '../components/messages/SystemMessage';
import TwilioFileMessage from '../components/messages/TwilioFileMessage';
import TextMessage from '../components/messages/TextMessage';
import FileImageMessage from '../components/messages/FileImageMessage';
import isSupportedImage from '../utils/isSupportedImage';
import TRANSLATIONS from '../translations';

// Noted: this will not work with seconds more as 24 hours like 90000 seconds.
const secondsToDuration = (seconds: number): string => moment.utc(seconds * 1000).format('HH:mm:ss');

const useTwilioMessage = (fallback = true) => {
  const { t } = useTranslation();
  const { isCurrentAttendee } = useCurrentAttendee();
  const { hideChatMillis } = useFirebaseChatConfig();
  const userEntities = useSelector(selectTwilioUserEntities);

  const genUserString = useCallback((userUuid: string): string => userEntities[userUuid]?.name
  || userUuid, [userEntities]);

  const getMessagePreview = useCallback((message: TwilioMessage): string => {
    if (!message || message.dateCreated.getTime() < hideChatMillis) return null;
    const messageType = message?.attributes?.messageType as MessageType | VideoCallMessageType;
    switch (messageType) {
      case MessageType.File:
        return t(...TRANSLATIONS.COMMON.FILE_PREVIEW);
      case MessageType.ContactCard:
        return t(...TRANSLATIONS.COMMON.CONTACT_CARD_PREVIEW);
      case MessageType.ParticipantJoined:
        return t(
          TRANSLATIONS.COMMON.PERSON_JOINED[0],
          `${genUserString(message?.attributes?.actionUuid)} joined`,
          { person: genUserString(message?.attributes?.actionUuid) },
        );
      case MessageType.ParticipantLeft:
        return t(
          TRANSLATIONS.COMMON.PERSON_LEFT[0],
          `${genUserString(message?.attributes?.actionUuid)} left`,
          { person: genUserString(message?.attributes?.actionUuid) },
        );
      case MessageType.VideoCall:
        return t(message.body, message.body);
      case VideoCallMessageType.VideoCallSummary:
        if (isNil(message.attributes.videoCallSummary?.duration)) {
          return t(message.body, message.body);
        }

        return t(
          TRANSLATIONS.VIDEO_CALL.DURATION[0],
          TRANSLATIONS.VIDEO_CALL.DURATION[1],
          { duration: secondsToDuration(message.attributes.videoCallSummary?.duration) },
        );
      default: return fallback ? message.body : null;
    }
  }, [fallback, genUserString, hideChatMillis, t]);

  const getMessageComponent = useCallback((message: TwilioMessage, direction = MessageDirection.INCOMING): ReactNode => {
    if (!message) return null;
    const messageType = message?.attributes?.messageType as MessageType | VideoCallMessageType;
    switch (messageType) {
      case MessageType.File: {
        const file = message.attributes?.file;
        const isImage = isSupportedImage(file);
        return isImage ? (
          <FileImageMessage
            direction={direction}
            message={message}
            file={file}
          />
        ) : (
          <DefaultBubbleWrapper
            direction={direction}
            index={message.index}
            dateCreated={message.dateCreated}
            displayNode={<TwilioFileMessage file={message.attributes?.file} />}
          />
        );
      }
      case MessageType.ContactCard:
        return (
          <DefaultBubbleWrapper
            direction={direction}
            index={message.index}
            dateCreated={message.dateCreated}
            displayNode={<ContactCard card={message.attributes.contact} messageDirection={direction} />}
          />
        );
      case MessageType.ParticipantJoined:
      case MessageType.ParticipantLeft:
        return (
          <SystemMessage
            index={message.index}
            content={getMessagePreview(message)}
          />
        );
      case VideoCallMessageType.VideoCallSummary:
        return (
          <DefaultBubbleWrapper
            direction={direction}
            index={message.index}
            dateCreated={message.dateCreated}
            displayNode={<TextMessage body={getMessagePreview(message)} />}
          />
        );
      case MessageType.VideoCall:
        return (
          <DefaultBubbleWrapper
            direction={direction}
            index={message.index}
            dateCreated={message.dateCreated}
            displayNode={<VideoCallMessage body={message.body} direction={direction} />}
          />
        );
      default: return fallback
        ? (
          <DefaultBubbleWrapper
            direction={direction}
            index={message.index}
            dateCreated={message.dateCreated}
            displayNode={<TextMessage body={message.body} />}
          />
        )
        : null;
    }
  }, [fallback, getMessagePreview]);

  const prefixSelf = useCallback((body: string, author: string): string => {
    if (!body || !author) return null;
    const sentBySelf = isCurrentAttendee(author);
    return sentBySelf
      ? t(
        TRANSLATIONS.COMMON.YOU_PREFIX[0],
        `You: ${body}`,
        { content: body },
      )
      : body;
  }, [t, isCurrentAttendee]);

  return useMemo(() => ({
    getMessagePreview,
    prefixSelf,
    getMessageComponent,
  }), [
    getMessagePreview,
    prefixSelf,
    getMessageComponent,
  ]);
};

export default useTwilioMessage;
